import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { serviceRequestsAPI, documentsAPI } from '@/lib/api';
import { Upload, CheckCircle, FileText, AlertCircle } from 'lucide-react';

const DocumentUploadPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [serviceRequest, setServiceRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadDoc, setCurrentUploadDoc] = useState('');
  const [uploadedFileNames, setUploadedFileNames] = useState({});
  const [documentPasswords, setDocumentPasswords] = useState({}); // Password for each document
  const [selectedFiles, setSelectedFiles] = useState({}); // Track selected file per document

  const loadServiceRequest = useCallback(async () => {
    try {
      const response = await serviceRequestsAPI.getById(requestId);
      setServiceRequest(response.data);
      
      // Initialize uploaded docs state
      const docs = {};
      response.data.documents.forEach(doc => {
        docs[doc] = true;
      });
      setUploadedDocs(docs);
    } catch (err) {
      console.error('Error loading service request:', err);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadServiceRequest();
  }, [loadServiceRequest]);

  const handleFileUpload = async (docType, file) => {
    if (!file) return;

    // Check file size based on file type
    const fileSizeMB = file.size / (1024 * 1024);
    const isZipFile = file.name.toLowerCase().endsWith('.zip');
    const MAX_FILE_SIZE_MB = isZipFile ? 25 : 5;
    
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      alert(`File size (${fileSizeMB.toFixed(2)}MB) exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB}MB${isZipFile ? ' for ZIP files' : ''}. Please compress or reduce the file size before uploading.`);
      return;
    }

    const isReplacing = uploadedDocs[docType] === true;
    const docPassword = documentPasswords[docType] || '';
    
    setUploading(true);
    setCurrentUploadDoc(isReplacing ? `Re-uploading ${docType}` : `Uploading ${docType}`);
    setUploadProgress(0);
    
    try {
      // Convert file to base64 with progress
      const reader = new FileReader();
      
      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          const percentLoaded = Math.round((e.loaded / e.total) * 50); // First 50% for reading
          setUploadProgress(percentLoaded);
        }
      };
      
      reader.onloadend = async () => {
        const base64Data = reader.result;
        setUploadProgress(50); // Reading complete
        
        try {
          const uploadData = {
            service_request_id: requestId,
            doc_type: docType,
            file_data: base64Data,
            original_filename: file.name  // Send original filename
          };
          
          // Add password if provided
          if (docPassword) {
            uploadData.password = docPassword;
          }
          
          await documentsAPI.upload(uploadData);
          
          setUploadProgress(100); // Upload complete
          setUploadedDocs({ ...uploadedDocs, [docType]: true });
          setUploadedFileNames({ ...uploadedFileNames, [docType]: file.name });
          
          // Clear the file input and password after successful upload
          setSelectedFiles({ ...selectedFiles, [docType]: null });
          
          // Small delay to show 100% before hiding
          setTimeout(() => {
            setUploading(false);
            setUploadProgress(0);
            setCurrentUploadDoc('');
          }, 500);
        } catch (uploadErr) {
          console.error('Error uploading to server:', uploadErr);
          alert(isReplacing ? 'Failed to re-upload document. Please try again.' : 'Failed to upload document. Please try again.');
          setUploading(false);
          setUploadProgress(0);
          setCurrentUploadDoc('');
        }
      };
      
      reader.onerror = () => {
        console.error('Error reading file');
        alert('Failed to read file. Please try again.');
        setUploading(false);
        setUploadProgress(0);
        setCurrentUploadDoc('');
      };
      
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error uploading document:', err);
      alert('Failed to upload document. Please try again.');
      setUploading(false);
      setUploadProgress(0);
      setCurrentUploadDoc('');
    }
  };

  const handleComplete = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const requiredDocs = serviceRequest?.service_details?.required_documents || [];
  const allDocsUploaded = requiredDocs.every(doc => uploadedDocs[doc]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" data-testid="upload-page-title">Upload Documents</h1>
          <p className="text-gray-600">Service: {serviceRequest?.service_details?.name}</p>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8" data-testid="upload-progress">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Upload Progress</h2>
            <span className="text-sm font-medium">
              {Object.keys(uploadedDocs).length} / {requiredDocs.length} completed
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${(Object.keys(uploadedDocs).length / requiredDocs.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Document Upload Cards */}
        <div className="space-y-4 mb-8">
          {requiredDocs.map((doc) => (
            <div 
              key={doc} 
              className="bg-white rounded-xl shadow-sm p-6 border-2 border-gray-200"
              data-testid={`doc-upload-card-${doc.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {uploadedDocs[doc] ? (
                    <CheckCircle className="text-green-500" size={32} />
                  ) : (
                    <FileText className="text-gray-400" size={32} />
                  )}
                </div>
                
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2">{doc}</h3>
                  
                  {uploadedDocs[doc] ? (
                    <>
                      <p className="text-sm text-green-600 mb-1">✓ Uploaded successfully</p>
                      {uploadedFileNames[doc] && (
                        <p className="text-xs text-gray-500 mb-3">File: {uploadedFileNames[doc]}</p>
                      )}
                      
                      {/* Password field - stays visible after upload */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Password (Optional)
                        </label>
                        <input
                          type="password"
                          value={documentPasswords[doc] || ''}
                          onChange={(e) => setDocumentPasswords({ ...documentPasswords, [doc]: e.target.value })}
                          placeholder="If document is password protected"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          data-testid={`password-input-${doc.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {documentPasswords[doc] ? 'Password saved with document' : 'Enter password if your document is protected'}
                        </p>
                      </div>
                      
                      {/* Re-upload button */}
                      <label className="cursor-pointer inline-block">
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.zip"
                          onChange={(e) => handleFileUpload(doc, e.target.files[0])}
                          className="hidden"
                          data-testid={`file-reupload-${doc.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                        <div className="bg-orange-600 text-white px-6 py-2.5 rounded-lg hover:bg-orange-700 transition flex items-center gap-2 text-sm font-medium">
                          <Upload size={18} />
                          Re-upload
                        </div>
                      </label>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 mb-3">Required document</p>
                      
                      {/* Password input (optional) */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Password (Optional)
                        </label>
                        <input
                          type="password"
                          value={documentPasswords[doc] || ''}
                          onChange={(e) => setDocumentPasswords({ ...documentPasswords, [doc]: e.target.value })}
                          placeholder="If document is password protected"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          data-testid={`password-input-${doc.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter password if your document is protected</p>
                      </div>
                      
                      {/* Upload button */}
                      <label className="cursor-pointer inline-block">
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.zip"
                          onChange={(e) => handleFileUpload(doc, e.target.files[0])}
                          className="hidden"
                          data-testid={`file-input-${doc.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                        <div className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition flex items-center gap-2 text-sm font-medium">
                          <Upload size={18} />
                          Upload
                        </div>
                      </label>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8" data-testid="upload-instructions">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-blue-600 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Upload Instructions</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Accepted formats: PDF, JPG, PNG, <strong>ZIP</strong></li>
                <li>• Maximum file size: <strong>25MB for ZIP files</strong>, 5MB for individual documents</li>
                <li>• You can upload all documents as a single ZIP file for convenience</li>
                <li>• Enter password if your documents/ZIP are password protected</li>
                <li>• Ensure all documents are clear and readable</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Complete Button */}
        {allDocsUploaded && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center" data-testid="completion-section">
            <CheckCircle className="text-green-600 mx-auto mb-4" size={48} />
            <h3 className="text-2xl font-bold text-green-900 mb-2">All Documents Uploaded!</h3>
            <p className="text-green-700 mb-6">Our CA team will review your documents shortly</p>
            <button
              onClick={handleComplete}
              className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition"
              data-testid="complete-button"
            >
              Back to Dashboard
            </button>
          </div>
        )}

        {uploading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
              <div className="text-center mb-6">
                <Upload className="text-blue-600 mx-auto mb-4" size={48} />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Uploading Document</h3>
                <p className="text-gray-600">{currentUploadDoc}</p>
              </div>
              
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Progress</span>
                  <span className="text-sm font-bold text-blue-600">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
              
              <p className="text-sm text-gray-500 text-center">
                {uploadProgress < 50 ? 'Reading file...' : uploadProgress < 100 ? 'Uploading to server...' : 'Complete!'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentUploadPage;